import { useTranslation } from 'react-i18next'

import type { Confidence } from '../lib/types'
import { Badge, Card, CardContent, CardHeader, CardTitle } from './ui'

const VARIANT: Record<Confidence, 'success' | 'warning' | 'danger'> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
}

export function ConfidenceBadge({
  level,
  score,
  showScore = false,
}: {
  level: Confidence
  score?: number
  showScore?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Badge variant={VARIANT[level]}>
      {t('common.confidence')}: {t(`common.${level}`)}
      {showScore && score !== undefined ? ` · ${score.toFixed(2)}` : ''}
    </Badge>
  )
}

/** Marca de dato simulado o estimado. Obligatoria: el cliente la exige. */
export function DataTag({
  isEstimate,
  datasetIds,
}: {
  isEstimate: boolean
  datasetIds: string[]
}) {
  const { t } = useTranslation()
  if (!isEstimate) return null
  return (
    <Badge variant="warning" title={datasetIds.join(', ')}>
      {t('common.simulated')}
    </Badge>
  )
}

/**
 * Lo que la respuesta NO puede decir.
 *
 * Va siempre visible, no en un desplegable: la limitacion mas importante es
 * la que impide una conclusion, y esconderla detras de un "¿por que?" es
 * como esa conclusion se acaba tomando igual.
 */
export function LimitationsPanel({
  limitations,
  missingIndicators,
}: {
  limitations: { code: string; text: Record<string, string>; severity: 'info' | 'warning' | 'critical' }[]
  missingIndicators: string[]
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  if (!limitations.length && !missingIndicators.length) return null

  return (
    <Card className="border-amber-500/30">
      <CardHeader>
        <CardTitle className="text-amber-600 dark:text-amber-400">
          {t('limitations.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {limitations.map((lim) => {
            const text = lim.text[locale] ?? lim.text.en ?? lim.code
            return (
              <li key={lim.code} className="flex items-start gap-2 text-sm">
                <span
                  className={
                    lim.severity === 'critical'
                      ? 'text-destructive mt-0.5 font-semibold'
                      : lim.severity === 'warning'
                        ? 'text-amber-600 dark:text-amber-400 mt-0.5 font-semibold'
                        : 'text-muted-foreground mt-0.5'
                  }
                >
                  {t(`limitations.severity.${lim.severity}`)}
                </span>
                <span>{text}</span>
              </li>
            )
          })}
          {missingIndicators.map((key) => (
            <li key={key} className="text-muted-foreground flex items-start gap-2 text-sm">
              <span className="mt-0.5">—</span>
              <span>
                <code className="text-xs">{key}</code>
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

/** Atribucion y frescura. La atribucion la produce el guard, no el texto. */
export function AttributionFooter({ answer }: { answer: { attribution: string; freshness: { status: string; oldest_age_days: number | null } } }) {
  const { t } = useTranslation()
  return (
    <div className="text-muted-foreground space-y-1 border-t pt-3 text-xs">
      {answer.attribution ? <p>{answer.attribution}</p> : null}
      <p>
        {t('common.freshness')}: {t(`common.${answer.freshness.status}`)}
        {answer.freshness.oldest_age_days !== null
          ? ` · ${answer.freshness.oldest_age_days} ${t('common.days')}`
          : ''}
      </p>
    </div>
  )
}
